import React, { useState } from 'react';
import Text from '../Text/Text';
import './LabeledValue.css';

interface LabeledValueProps {
  label: string;
  value: string | number | null | undefined;
  className?: string;
  /**
   * Controls whether the label and value appear on the same line or with the value on a new line
   * @default false
   */
  multiline?: boolean;
  /**
   * Whether to show a copy button next to the value
   * @default false
   */
  copyable?: boolean;
  /**
   * Callback function to be called when the value is copied
   */
  onCopy?: () => void;
  /**
   * Whether to right-align the value
   * @default false
   */
  rightAligned?: boolean;
  /**
   * Whether to align the value in a column (useful for financial data)
   * @default false
   */
  columnAligned?: boolean;
  /**
   * Whether the component is in a loading state
   * @default false
   */
  isLoading?: boolean;
  /**
   * Custom loading text to display
   * @default "Loading..."
   */
  loadingText?: string;
}

/**
 * A reusable component for displaying a label-value pair
 * with consistent styling and error handling
 */
const LabeledValue: React.FC<LabeledValueProps> = ({
  label,
  value,
  className = "text-body",
  multiline = false,
  copyable = false,
  onCopy,
  rightAligned = false,
  columnAligned = false,
  isLoading = false,
  loadingText = "Loading..."
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(String(value))
        .then(() => {
          setIsCopied(true);
          if (onCopy) onCopy();
          
          // Reset the copied state after 2 seconds
          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  const renderCopyButton = () => {
    if (!copyable || value === null || value === undefined || isLoading) return null;
    
    return (
      <button
        className={`copy-button ${isCopied ? 'copied' : ''}`}
        onClick={handleCopy}
        aria-label="Copy to clipboard"
      >
        {isCopied ? 'Copied!' : 'Copy'}
      </button>
    );
  };

  // Determine the display value
  const displayValue = isLoading ? loadingText : (value ?? 'N/A');
  if (multiline) {
    return (
      <div className={`labeled-value labeled-value-multiline ${className}`}>
        <Text className={className}>
          <strong aria-label={label}>{label}:</strong>
        </Text>
        <div className="labeled-value-content">
          <Text className={`${className} ${isLoading ? 'loading-text' : ''}`}>
            {displayValue}
          </Text>
          {renderCopyButton()}
        </div>
      </div>
    );
  }

  return (
    <div className={`labeled-value-inline ${className} ${rightAligned ? 'right-aligned' : ''} ${columnAligned ? 'column-aligned' : ''}`}>
      {label && (
        <Text className={className} >
          <strong aria-label={label}>{label}:</strong>
        </Text>
      )}
      <Text className={`${className} ${columnAligned ? 'value-column' : ''} ${isLoading ? 'loading-text' : ''}`}>
        {displayValue}
      </Text>
      {renderCopyButton()}
    </div>
  );
};

export default LabeledValue;